// dates are a mess in Javascript, let's make them better
// helpful resource: https://css-tricks.com/everything-you-need-to-know-about-date-in-javascript/
//
// This module owns two concerns for our common date shapes (js Date,
// YYYY-MM-DD datestring, ISO timestring, pipedrive timestring, millisecond
// epoch):
//   1. Parsing/conversion (toDate, toDateString, …)
//   2. Display formatting (format, formatDate, timeAgo, …)

import { format as fnsFormat, formatDistanceToNow } from "date-fns"
import { isNumeric } from "./number"

const regexps = {
  dateString: /^\d{4}-\d{1,2}-\d{1,2}$/,
  isoString: /^\d{4}-\d{1,2}-\d{1,2}T\d{2}:\d{2}:\d{2}/,
  millisecondEpoch: /^\d{12,13}$/,
  pipedriveTime: /^\d{4}-\d{1,2}-\d{1,2}\s\d{2}:\d{2}:\d{2}$/,
}

// take a YYYY-MM-DD string and return an object with day, month, year properties
export const dateStringInChunks = (dateStr: any) => {
  const [year, month, day] = dateStr.split("-")
  return { year, month, day }
}

export const isDate = (obj: any) => {
  return obj instanceof Date
}

export const isDateString = (obj: any) => {
  return regexps.dateString.test(obj)
}

// create a js date representing the current time
export const now = () => {
  return new Date()
}

// universal converter across our common formats to a local-time js date object. currently
// accepts js dates, YYYY-MM-DD datestrings, ISO timestrings, pipedrive timestrings and
// millisecond epochs (12–13 digits; seconds-resolution epochs are rejected).
export const toDate = (dateOrDateString: any) => {
  const input = dateOrDateString
  if (isDate(input)) return input
  // some kind of date string or epoch. TZ handling varies by shape: datestrings
  // parse as LOCAL midnight, pipedrive timestrings are assumed UTC, ISO strings
  // carry their own TZ offset (and parse as local when the offset is omitted).
  if (regexps.dateString.test(input)) {
    return _dateStringToDate(input)
  } else if (regexps.isoString.test(input)) {
    return new Date(input) // date can parse these
  } else if (regexps.pipedriveTime.test(input)) {
    return _pipedriveTimeToDate(input)
  } else if (isNumeric(input) && regexps.millisecondEpoch.test(input)) {
    return new Date(Number(input)) // epoch may arrive as a string; Date needs a number
  } else {
    throw new Error(`Unknown date format: ${JSON.stringify(input)}`)
  }
}

// convert a js date to a YYYY-MM-DD datestring (local timezone)
export const toDateString = (dateOrDateString: any) => {
  if (regexps.dateString.test(dateOrDateString)) return dateOrDateString
  const date = toDate(dateOrDateString)
  const month = (date.getMonth() + 1).toString().padStart(2, "0")
  const day = date.getDate().toString().padStart(2, "0")
  return date.getUTCFullYear() + "-" + month + "-" + day
}

// convert a js date to a YYYY-MM-DD datestring (UTC)
export const toUTCDateString = (dateOrDateString: any) => {
  const date = toDate(dateOrDateString)
  return date.toISOString().split("T")[0]
}

// YYYY-MM-DD date string for the current date in local timezone
export const today = () => {
  return toDateString(new Date())
}

const _dateStringToDate = (dateString: any) => {
  const chunks: any = dateStringInChunks(dateString)
  return new Date(chunks.year, chunks.month - 1, chunks.day)
}

const _pipedriveTimeToDate = (pipedriveTime: any) => {
  const [dateString, timeString] = pipedriveTime.split(" ")
  return new Date(dateString + "T" + timeString + "Z")
}

// --- Display formatting ---
//
// Everything here takes any of our common date shapes and returns a display
// string. Invalid-input contract — these functions never throw (a throw inside
// a Vue computed kills the whole component render, worse than a blank for
// display code):
// - empty input (null, undefined, "") returns "" silently — absence is a
//   normal display state.
// - unparseable input (garbage strings, Invalid Date instances, NaN,
//   seconds-resolution epochs, epoch 0 — toDate only accepts 12–13-digit
//   millisecond epochs) also returns "", but logs a console.error so
//   upstream bugs aren't invisible.

export type DateInput = Date | string | number | null | undefined

// implements the invalid-input contract above: Date on success, null (blank
// display) otherwise, logging every failure that isn't plain absence.
const parseForDisplay = (input: DateInput): Date | null => {
  if (input == null || input === "") return null
  let date: Date | undefined
  try {
    date = toDate(input)
  } catch {
    // unknown shape — fall through to the error log below
  }
  if (!date || isNaN(date.getTime())) {
    console.error("[date] unparseable date input:", input)
    return null
  }
  return date
}

// escape hatch — format with any date-fns format string:
// https://date-fns.org/v4.1.0/docs/format
export const format = (dateOrDateString: DateInput, formatString: string): string => {
  const date = parseForDisplay(dateOrDateString)
  return date ? fnsFormat(date, formatString) : ""
}

// ex: Feb 14, 2019
export const formatDate = (dateOrDateString: DateInput): string => {
  return format(dateOrDateString, "MMM d, yyyy")
}

// ex: Feb 14, 2019, 3:04 PM
export const formatDateTime = (dateOrDateString: DateInput): string => {
  return format(dateOrDateString, "MMM d, yyyy, h:mm a")
}

// ex: October 2018
export const formatMonthYear = (dateOrDateString: DateInput): string => {
  return format(dateOrDateString, "MMMM yyyy")
}

// ex: Oct 2018
export const formatShortMonthYear = (dateOrDateString: DateInput): string => {
  return format(dateOrDateString, "MMM yyyy")
}

// ex: 10/2018
export const formatNumericMonthYear = (dateOrDateString: DateInput): string => {
  return format(dateOrDateString, "MM/yyyy")
}

// ex: Q2 2019
export const formatQuarter = (dateOrDateString: DateInput): string => {
  return format(dateOrDateString, "qqq yyyy")
}

// ex: 2 minutes ago; 3 months ago; in 4 days
export const timeAgo = (dateOrDateString: DateInput): string => {
  const date = parseForDisplay(dateOrDateString)
  return date ? formatDistanceToNow(date, { addSuffix: true }) : ""
}

// ex: just now; 5m ago; 3h ago; 2d ago — then the absolute date past a week.
// The compact counterpart to timeAgo, for dense lists (record history) where
// "about 2 hours ago" would wrap the row.
export const timeAgoShort = (dateOrDateString: DateInput): string => {
  const date = parseForDisplay(dateOrDateString)
  if (!date) return ""
  const minutes = Math.floor((Date.now() - date.getTime()) / 60000)
  if (minutes < 1) return "just now"
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return formatDate(date)
}

// --- Timezone-aware display/parsing (record_times) ---
//
// The record_times model (TimesList.vue/TimeEditor.vue) stores an explicit
// IANA zone per row and must render/parse against THAT zone rather than
// wherever the viewer happens to be — unlike everything else in this module,
// which is deliberately browser-local. Extracted from those two components
// (2026-08-03); see F10 in docs/superpowers/formatter-sprawl-audit.md.
//
// Invalid-zone contract: `tz` ultimately comes from TimeEditor's free-text
// input (no validation), so every function below falls back to the browser's
// zone on a bad IANA name rather than throwing — matching what the original
// inline code's own comments claimed but didn't actually implement (their
// try/catch wrapped the options-object assignment, which never throws; the
// real RangeError comes from the Intl call itself and went uncaught).

// The IANA zone the browser is currently running in — the default for a new
// record_times row before the user picks one.
export const getBrowserTimeZone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone

// "YYYY-MM-DD" for the given instant, evaluated in `tz` (en-CA renders
// YYYY-MM-DD unambiguously). Used both standalone (same-day comparison in
// TimesList's time-range display) and as the date half of parseInZone's
// counterpart, formatInZone's date parts.
export const getDateStringInZone = (dateOrDateString: DateInput, tz?: string | null): string => {
  const date = parseForDisplay(dateOrDateString)
  if (!date) return ""
  const opts: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }
  try {
    return new Intl.DateTimeFormat("en-CA", {
      ...opts,
      timeZone: tz || undefined,
    }).format(date)
  } catch {
    return new Intl.DateTimeFormat("en-CA", opts).format(date)
  }
}

// 24-hour "HH:MM" for the given instant, evaluated in `tz`. Same invalid-zone
// fallback as getDateStringInZone.
export const getTimeStringInZone = (dateOrDateString: DateInput, tz?: string | null): string => {
  const date = parseForDisplay(dateOrDateString)
  if (!date) return ""
  const opts: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }
  try {
    return new Intl.DateTimeFormat("en-GB", {
      ...opts,
      timeZone: tz || undefined,
    }).format(date)
  } catch {
    return new Intl.DateTimeFormat("en-GB", opts).format(date)
  }
}

// Interpret dateStr ("YYYY-MM-DD") + timeStr ("HH:MM") as wall-clock time IN
// `tz`, returning the equivalent UTC ISO instant — the inverse of
// getDateStringInZone/getTimeStringInZone. Used to turn TimeEditor's date/time
// inputs (always displayed in the record's zone) back into a storable UTC
// timestamp.
//
// Fixed-point approach (matches libraries like date-fns-tz/Luxon): treat the
// wall-clock string as a first-guess UTC instant, ask what wall-clock time
// that instant reads as IN `tz`, and use the difference as `tz`'s offset —
// repeated once more so a DST-boundary offset shift between the guess and the
// true instant self-corrects. This replaced (2026-08-03) a version that
// instead ran the wall-clock string through the LOCAL `Date` constructor
// (parsed in whatever zone the browser itself is in) before subtracting only
// the target zone's offset — correct only when the viewer's own browser
// happened to be in UTC, silently wrong by the viewer's own UTC offset
// otherwise. That bug was live in production; see F10 in
// docs/superpowers/formatter-sprawl-audit.md for the incident writeup.
export const parseInZone = (dateStr: string, timeStr: string, tz: string): string => {
  const wallClock = `${dateStr}T${timeStr}:00`
  let guessMs = Date.parse(wallClock + "Z")
  if (isNaN(guessMs)) return new Date(NaN).toISOString()

  let formatter: Intl.DateTimeFormat
  try {
    formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    })
  } catch {
    return new Date(guessMs).toISOString()
  }

  for (let i = 0; i < 2; i++) {
    const parts = formatter.formatToParts(new Date(guessMs))
    const get = (type: string) => parts.find((p) => p.type === type)?.value
    let hour = Number(get("hour"))
    if (hour === 24) hour = 0 // some locales render midnight as "24:00"
    const zonedAsUtc = Date.UTC(
      Number(get("year")),
      Number(get("month")) - 1,
      Number(get("day")),
      hour,
      Number(get("minute")),
      Number(get("second"))
    )
    const offsetMs = zonedAsUtc - guessMs
    guessMs = Date.parse(wallClock + "Z") - offsetMs
  }
  return new Date(guessMs).toISOString()
}

export type ZoneDisplayStyle = "date" | "datetime" | "time"

// Display a datetime in `tz` using the browser's locale. `style` picks
// "medium date", "medium date + short time", or "short time only" — the three
// shapes TimesList.vue's time-range display needs. Falls back to the
// browser's zone on an invalid `tz`.
export const formatInZone = (
  dateOrDateString: DateInput,
  tz: string | null | undefined,
  style: ZoneDisplayStyle = "datetime"
): string => {
  const date = parseForDisplay(dateOrDateString)
  if (!date) return ""
  const opts: Intl.DateTimeFormatOptions =
    style === "date"
      ? { dateStyle: "medium" }
      : style === "time"
        ? { timeStyle: "short" }
        : { dateStyle: "medium", timeStyle: "short" }

  const render = (o: Intl.DateTimeFormatOptions) =>
    style === "time" ? date.toLocaleTimeString(undefined, o) : date.toLocaleString(undefined, o)

  try {
    return render({ ...opts, timeZone: tz || undefined })
  } catch {
    return render(opts)
  }
}

// Short zone abbreviation for display (e.g. "PST", "JST"). Falls back to the
// raw IANA name if formatting fails (invalid zone, etc).
export const getZoneLabel = (tz?: string | null): string => {
  if (!tz) return ""
  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "short",
    })
    const parts = formatter.formatToParts(new Date())
    return parts.find((p) => p.type === "timeZoneName")?.value || tz
  } catch {
    return tz
  }
}

export default {
  dateStringInChunks,
  isDate,
  isDateString,
  now,
  toDate,
  toDateString,
  toUTCDateString,
  today,
  format,
  formatDate,
  formatDateTime,
  formatMonthYear,
  formatShortMonthYear,
  formatNumericMonthYear,
  formatQuarter,
  timeAgo,
  getBrowserTimeZone,
  getDateStringInZone,
  getTimeStringInZone,
  parseInZone,
  formatInZone,
  getZoneLabel,
}
